import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { Button } from 'components/Button/Button';
import { useState } from 'react';
import type { FC } from 'react';
import { useApi } from 'utils/request/useApi';

const REQUESTS = 20;

export const RateLimitingTestGallerySection: FC = () => {
  const api = useApi();
  const [status, setStatus] = useState<'initial' | 'loading' | 'ok' | 'error'>(
    'initial',
  );

  const fireInTheHole = async () => {
    try {
      setStatus('loading');
      await Promise.all(
        [...Array(REQUESTS)].map(() => api.request({ url: '/dev/timeout/0' })),
      );
      setStatus('ok');
    } catch (e) {
      setStatus('error');
      throw e;
    }
  };

  return (
    <div>
      <p>
        Normally, frontend should recover from HTTP 429 by repeating the request
        later.
      </p>
      <p>
        Assuming you are on a development machine, follow the steps below to
        test rate limiting handling:
      </p>
      <ol>
        <li>
          In <code>config/development.json</code> in section <code>app</code>,
          set <code>&quot;maxRequestsPerSecond&quot;: 5</code>.
        </li>
        <li>
          Restart api <code>sudo service ipf-api restart</code>
        </li>
        <li>Have browser developer console open</li>
        <li>Once api is up again, push the button below.</li>
      </ol>
      <p>
        If rate limiting handling works properly on frontend, you should see
        green success message after few seconds, while in developer
        console&apos;s Network tab there are requests failing with HTTP 429.
      </p>
      <p className='text-gray-800'>
        Note: by default, maxRequestsPerSecond is set to 50. That is difficult
        to test, because Chrome seems to do some rate limiting itself and we did
        not manage to make it flood the default rate when developing this test.
      </p>
      <Button variant='primary' onClick={fireInTheHole} type='button'>
        Start {REQUESTS} API requests
      </Button>
      {status === 'loading' && (
        <p>
          <Icon inline className='fa-spin' glyph='fa-spinner' />
          It&apos;s raining requests, hallelujah!
        </p>
      )}
      {status === 'ok' && (
        <p className='text-green-500'>{REQUESTS} requests fired successfully</p>
      )}
      {status === 'error' && (
        <p className='text-red-500'>
          {REQUESTS} requests failed, see console to inspect the error
        </p>
      )}
    </div>
  );
};
